import React from 'react';
import { Formik, Form } from 'formik';
import { Grid } from '@mui/material';
import type { GridProps } from '@mui/material';
import type { FormikHelpers, FormikValues } from 'formik';
import type { AnyObjectSchema } from 'yup';

import * as Yup from 'yup';
interface SchemaProviderProps<FormValues extends FormikValues> {
  validationSchema: Yup.ObjectSchema<FormValues>;
  children: React.ReactNode;
}

interface SchemaProviderContextType {
  validationSchema: Yup.ObjectSchema<any>;
}

const SchemaProviderContext = React.createContext<SchemaProviderContextType>({
  validationSchema: Yup.object().shape({}),
});

export const useSchemaContext = () => React.useContext(SchemaProviderContext);

export const SchemaProvider = <FormValues extends FormikValues>({
  validationSchema,
  children,
}: SchemaProviderProps<FormValues>) => {
  const contextValue = {
    validationSchema,
  };

  return (
    <SchemaProviderContext.Provider value={contextValue}>
      {children}
    </SchemaProviderContext.Provider>
  );
};

export type SparkFormProps<Values extends FormikValues> = {
  children: React.ReactNode;
  initialValues: Values;
  enableReinitialize?: boolean;
  onSave: (
    values: Values,
    formikHelpers: FormikHelpers<Values>
  ) => void | Promise<unknown>;
  validationSchema: AnyObjectSchema;
  containerProps?: Omit<GridProps, 'sx'>;
  sx?: GridProps['sx'];
};

function SparkForm<Values extends FormikValues>({
  children,
  initialValues,
  onSave,
  validationSchema,
  enableReinitialize = true,
  containerProps = {},
  sx = {},
}: SparkFormProps<Values>) {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSave}
      validationSchema={validationSchema}
      enableReinitialize={enableReinitialize}
      validateOnMount
    >
      <SchemaProvider<FormikValues> validationSchema={validationSchema}>
        <Form style={{ width: '100%', height: '100%' }}>
          <Grid
            container
            direction='row'
            alignContent='flex-start'
            {...containerProps}
            sx={{ height: '100%', ...sx }}
          >
            {children}
          </Grid>
        </Form>
      </SchemaProvider>
    </Formik>
  );
}

export default SparkForm;
